(() => {
  const observedRoots = new WeakSet();
  const carouselStates = new WeakMap();
  const debug = (...args) => console.info('[Boring Moast scrollbar]', ...args);

  debug('override loaded');

  const addScrollbarStyles = () => {
    if (document.querySelector('style[data-boring-moast-scrollbar]')) return;

    const style = document.createElement('style');
    style.dataset.boringMoastScrollbar = '';
    style.textContent = `
      .boring-moast-scrollbar {
        background: rgb(24 22 22 / 0.35);
        cursor: pointer;
        height: 1px;
        margin: 0rem 1.5rem 0;
        overflow: visible;
        position: relative;
        touch-action: pan-y;
      }

      .boring-moast-scrollbar__thumb {
        background: #181616;
        display: block;
        height: 1px;
        left: 0;
        min-width: 2px;
        position: absolute;
        pointer-events: none;
        top: 0;
        transform: translate3d(0, 0, 0);
        transform-origin: left center;
        will-change: transform;
      }

      @media screen and (min-width: 860px) {
        .boring-moast-scrollbar {
          margin-left: 2rem;
          margin-right: 2rem;
        }
      }
    `;
    document.head.appendChild(style);
  };

  const getShadowElements = (root) => {
    const elements = [];

    root.querySelectorAll('*').forEach((element) => {
      elements.push(element);
      if (element.shadowRoot) elements.push(...getShadowElements(element.shadowRoot));
    });

    return elements;
  };

  const findHorizontalScroller = (carousel) => {
    const roots = [carousel];
    if (carousel.shadowRoot) roots.push(carousel.shadowRoot);

    const candidates = roots.flatMap(getShadowElements).filter((element) => {
      if (!(element instanceof HTMLElement)) return false;

      const overflowX = getComputedStyle(element).overflowX;
      const isNativeScroller = overflowX === 'auto' || overflowX === 'scroll';
      return isNativeScroller && element.clientWidth > 0 && element.scrollWidth > element.clientWidth + 2;
    });

    return candidates.sort((a, b) => (
      (b.scrollWidth - b.clientWidth) - (a.scrollWidth - a.clientWidth)
    ))[0];
  };

  const updateScrollbar = (state, resizeThumb = false) => {
    const { scroller, scrollbar, thumb } = state;
    const scrollableWidth = scroller ? scroller.scrollWidth - scroller.clientWidth : 0;

    if (scroller && scrollableWidth <= 2) {
      scrollbar.hidden = true;
      return;
    }

    scrollbar.hidden = false;

    const trackWidth = scrollbar.clientWidth;
    if (resizeThumb || !state.thumbRatio) {
      state.thumbRatio = scroller
        ? Math.min(1, scroller.clientWidth / scroller.scrollWidth)
        : 0.25;
    }

    const thumbWidth = Math.max(2, trackWidth * state.thumbRatio);
    const nativeProgress = scroller ? scroller.scrollLeft / scrollableWidth : 0;
    const progress = Math.min(1, Math.max(0, state.virtualProgress ?? nativeProgress));
    const thumbOffset = progress * (trackWidth - thumbWidth);

    thumb.style.width = `${thumbWidth}px`;
    thumb.style.transform = `translate3d(${thumbOffset}px, 0, 0)`;
  };

  const getScrollbarProgress = (state) => {
    const { scroller } = state;
    if (!scroller) return state.virtualProgress ?? 0;

    const scrollableWidth = scroller.scrollWidth - scroller.clientWidth;
    if (scrollableWidth <= 2) return 0;

    return Math.min(1, Math.max(0, state.virtualProgress ?? (scroller.scrollLeft / scrollableWidth)));
  };

  const scrollToPointerPosition = (state, clientX) => {
    const { scroller, scrollbar } = state;
    if (!scroller || !scrollbar.clientWidth) return;

    const scrollableWidth = scroller.scrollWidth - scroller.clientWidth;
    if (scrollableWidth <= 2) return;

    const track = scrollbar.getBoundingClientRect();
    const thumbWidth = Math.max(2, scrollbar.clientWidth * (state.thumbRatio || 0));
    const availableTrack = Math.max(1, scrollbar.clientWidth - thumbWidth);
    const offset = Math.min(
      availableTrack,
      Math.max(0, clientX - track.left - thumbWidth / 2),
    );

    scroller.scrollLeft = (offset / availableTrack) * scrollableWidth;
    state.virtualProgress = null;
    updateScrollbar(state);
  };

  const setScroller = (state, scroller) => {
    if (!state || !scroller || state.scroller === scroller) {
      if (state?.scroller) updateScrollbar(state);
      return;
    }

    state.scroller?.removeEventListener('scroll', state.onScroll);
    state.resizeObserver?.disconnect();

    state.scroller = scroller;
    debug('connected native scroller', {
      tag: scroller.tagName,
      className: scroller.className,
      clientWidth: scroller.clientWidth,
      scrollWidth: scroller.scrollWidth,
    });
    state.onScroll = () => {
      state.virtualProgress = null;
      if (!state.loggedNativeScroll) {
        state.loggedNativeScroll = true;
        debug('native scroll event received');
      }
      requestAnimationFrame(() => updateScrollbar(state));
    };
    state.onResize = () => requestAnimationFrame(() => updateScrollbar(state, true));
    state.resizeObserver = new ResizeObserver(state.onResize);

    scroller.addEventListener('scroll', state.onScroll, { passive: true });
    state.resizeObserver.observe(scroller);
    state.resizeObserver.observe(state.scrollbar);
    updateScrollbar(state, true);
  };

  const connectScrollbar = (carousel) => {
    const state = carouselStates.get(carousel);
    const scroller = findHorizontalScroller(carousel);
    if (!scroller && !state?.loggedMissingScroller) {
      state.loggedMissingScroller = true;
      debug('no native horizontal scroller found');
    }
    setScroller(state, scroller);
  };

  const addScrollbar = (carousel) => {
    if (carouselStates.has(carousel)) return;

    addScrollbarStyles();

    const scrollbar = document.createElement('div');
    const thumb = document.createElement('span');
    scrollbar.className = 'boring-moast-scrollbar';
    scrollbar.setAttribute('aria-hidden', 'true');
    thumb.className = 'boring-moast-scrollbar__thumb';
    thumb.style.width = '25%';
    scrollbar.appendChild(thumb);
    carousel.insertAdjacentElement('afterend', scrollbar);

    const state = { scrollbar, thumb, activePointerId: null, virtualProgress: null };
    carouselStates.set(carousel, state);
    debug('scrollbar attached', carousel);

    state.onPointerDown = (event) => {
      if (!state.scroller) return;

      event.preventDefault();
      state.activePointerId = event.pointerId;
      scrollbar.setPointerCapture?.(event.pointerId);
      scrollToPointerPosition(state, event.clientX);
    };

    state.onPointerMove = (event) => {
      if (state.activePointerId !== event.pointerId) return;
      scrollToPointerPosition(state, event.clientX);
    };

    state.onPointerEnd = (event) => {
      if (state.activePointerId !== event.pointerId) return;
      state.activePointerId = null;
      if (scrollbar.hasPointerCapture?.(event.pointerId)) {
        scrollbar.releasePointerCapture(event.pointerId);
      }
    };

    scrollbar.addEventListener('pointerdown', state.onPointerDown);
    scrollbar.addEventListener('pointermove', state.onPointerMove);
    scrollbar.addEventListener('pointerup', state.onPointerEnd);
    scrollbar.addEventListener('pointercancel', state.onPointerEnd);

    state.onCarouselPointerDown = (event) => {
      if (event.pointerType === 'mouse' && event.button !== 0) return;

      debug('carousel pointer down', { pointerType: event.pointerType, scrollLeft: state.scroller?.scrollLeft ?? null });
      state.carouselPointer = {
        id: event.pointerId,
        startX: event.clientX,
        startScrollLeft: state.scroller?.scrollLeft ?? 0,
        startProgress: getScrollbarProgress(state),
      };
    };

    state.onCarouselPointerMove = (event) => {
      const pointer = state.carouselPointer;
      if (!pointer || pointer.id !== event.pointerId) return;

      if (state.scroller && Math.abs(state.scroller.scrollLeft - pointer.startScrollLeft) > 1) return;

      const carouselWidth = Math.max(carousel.clientWidth, scrollbar.clientWidth);
      const scrollableWidth = state.scroller
        ? state.scroller.scrollWidth - state.scroller.clientWidth
        : carouselWidth * 2;
      const dragRange = Math.max(1, scrollableWidth, carouselWidth);
      state.virtualProgress = Math.min(
        1,
        Math.max(0, pointer.startProgress - ((event.clientX - pointer.startX) / dragRange)),
      );
      if (!state.loggedDragFallback) {
        state.loggedDragFallback = true;
        debug('transform-drag fallback active', { virtualProgress: state.virtualProgress });
      }
      updateScrollbar(state);
    };

    state.onCarouselPointerEnd = (event) => {
      if (state.carouselPointer?.id === event.pointerId) state.carouselPointer = null;
    };

    carousel.addEventListener('pointerdown', state.onCarouselPointerDown, { capture: true, passive: true });
    carousel.addEventListener('pointermove', state.onCarouselPointerMove, { capture: true, passive: true });
    carousel.addEventListener('pointerup', state.onCarouselPointerEnd, { capture: true, passive: true });
    carousel.addEventListener('pointercancel', state.onCarouselPointerEnd, { capture: true, passive: true });

    state.onCarouselWheel = (event) => {
      const delta = Math.abs(event.deltaX) > Math.abs(event.deltaY)
        ? event.deltaX
        : event.deltaY;

      if (!delta) return;

      const carouselWidth = Math.max(carousel.clientWidth, scrollbar.clientWidth, 1);
      const scrollableWidth = state.scroller
        ? Math.max(1, state.scroller.scrollWidth - state.scroller.clientWidth)
        : carouselWidth * 2;

      state.virtualProgress = Math.min(
        1,
        Math.max(0, getScrollbarProgress(state) + (delta / scrollableWidth)),
      );
      updateScrollbar(state);
    };

    carousel.addEventListener('wheel', state.onCarouselWheel, { passive: true });

    ['mousedown', 'touchstart', 'pointerdown'].forEach((eventName) => {
      carousel.addEventListener(eventName, () => {
        debug('raw carousel input received', eventName);
      }, { capture: true, passive: true });
    });

    state.mutationObserver = new MutationObserver(() => connectScrollbar(carousel));
    state.mutationObserver.observe(carousel, {
      attributes: true,
      childList: true,
      subtree: true,
    });

    state.hostResizeObserver = new ResizeObserver(() => connectScrollbar(carousel));
    state.hostResizeObserver.observe(carousel);
    window.addEventListener('load', () => connectScrollbar(carousel), { once: true });

    connectScrollbar(carousel);
  };

  const addOverride = (root) => {
    if (!root || root.querySelector('style[data-boring-moast-overrides]')) return;

    const style = document.createElement('style');
    style.dataset.boringMoastOverrides = '';
    style.textContent = `
      .play-button-container,
      .pause-button-container,
      .play-button,
      .pause-button,
      moast-play {
        display: none !important;
      }
    `;
    root.appendChild(style);
  };

  const observeTree = (root, carousel) => {
    if (!root || observedRoots.has(root)) return;
    observedRoots.add(root);

    if (root instanceof ShadowRoot) addOverride(root);

    root.addEventListener('scroll', (event) => {
      const scroller = event.target;
      if (!(scroller instanceof HTMLElement)) return;
      const overflowX = getComputedStyle(scroller).overflowX;
      if (overflowX !== 'auto' && overflowX !== 'scroll') return;
      if (scroller.clientWidth <= 0 || scroller.scrollWidth <= scroller.clientWidth + 2) return;

      setScroller(carouselStates.get(carousel), scroller);
    }, true);

    root.querySelectorAll('*').forEach((element) => {
      if (element.shadowRoot) observeTree(element.shadowRoot, carousel);
    });

    new MutationObserver((mutations) => {
      mutations.forEach(({ addedNodes }) => {
        addedNodes.forEach((node) => {
          if (!(node instanceof Element)) return;
          if (node.shadowRoot) observeTree(node.shadowRoot, carousel);
          node.querySelectorAll('*').forEach((element) => {
            if (element.shadowRoot) observeTree(element.shadowRoot, carousel);
          });
        });
      });

      connectScrollbar(carousel);
    }).observe(root, { attributes: true, childList: true, subtree: true });
  };

  const observeCarousel = (carousel) => {
    addScrollbar(carousel);

    if (carousel.shadowRoot) {
      observeTree(carousel.shadowRoot, carousel);
      connectScrollbar(carousel);
      return;
    }

    customElements.whenDefined('moast-carousel').then(() => {
      requestAnimationFrame(() => {
        observeTree(carousel.shadowRoot, carousel);
        connectScrollbar(carousel);
      });
    });
  };

  document.querySelectorAll('moast-carousel').forEach(observeCarousel);

  new MutationObserver((mutations) => {
    mutations.forEach(({ addedNodes }) => {
      addedNodes.forEach((node) => {
        if (!(node instanceof Element)) return;
        if (node.matches('moast-carousel')) observeCarousel(node);
        node.querySelectorAll('moast-carousel').forEach(observeCarousel);
      });
    });
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
